<template>
	<div class="">
		<div class="flex justify-between mb-4">
			<h2 class="text-base font-medium">
				{{ setName ? capitalize(setName) : "" }}:
				{{ selectedSize?.Name || selectedSize?.Code }}
			</h2>
		</div>
		<RadioGroup :id="`${sizes?.[0]?.ParametricCode}`" v-model="selectedSize">
			<RadioGroupLabel
				:id="`label-${sizes?.[0]?.ParametricCode}`"
				class="sr-only"
			>
				{{ setName ? capitalize(setName) : "" }}
				{{ selectedSize?.Name || selectedSize?.Code }}
			</RadioGroupLabel>
			<div class="flex flex-wrap gap-4 2xl:max-w-full md:max-w-lg">
				<RadioGroupOption
					v-for="size in sizes"
					:id="`${size?.ParametricCode}-${size?.ParametricId}-${size?.Id}`"
					:key="size?.Id"
					v-slot="{ checked }"
					as="template"
					:value="size"
				>
					<div
						:class="[
							'bg-white text-gray-900 cursor-pointer',
							'group relative flex items-center justify-center py-3.5 px-4 text-sm font-medium uppercase hover:bg-gray focus:outline-none flex-1 max-w-[56px]'
						]"
					>
						<RadioGroupLabel
							:id="`${size?.ParametricCode}-${size?.Id}`"
							class="z-10"
							as="span"
							:class="[size?.InStock ? 'text-gray-900' : 'text-gray-dark']"
						>
							{{ size?.Code }}
						</RadioGroupLabel>
						<span
							:class="[
								checked
									? 'ring-2 ring-gray-super-dark border-transparent'
									: 'border-gray-dark',
								'shadow-sm border pointer-events-none absolute -inset-px'
							]"
							aria-hidden="true"
						/>
						<span
							v-if="!size?.InStock"
							aria-hidden="true"
							class="pointer-events-none absolute -inset-px rounded-md border-2 border-gray-dark bg-gray-light"
						>
							<svg
								class="absolute inset-0 h-full w-full stroke-2 text-gray-dark"
								viewBox="0 0 100 100"
								preserveAspectRatio="none"
								stroke="currentColor"
							>
								<line
									x1="0"
									y1="100"
									x2="100"
									y2="0"
									vector-effect="non-scaling-stroke"
								/>
							</svg>
						</span>
					</div>
				</RadioGroupOption>
			</div>
		</RadioGroup>
	</div>
</template>

<script setup lang="ts">
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import type {
	ParametricListValue,
	ParametricListValuewithCount
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { capitalize, computed } from "vue";
import { useRoute } from "vue-router";

interface Props {
	parametricsSecondOne:
		| (ParametricListValue | ParametricListValuewithCount)[]
		| undefined;
}
const { parametricsSecondOne } = defineProps<Props>();

const { getAllSecondParametrics, getFirstSecondParametricWithStock } =
	useNorcePwa();

const sizes = ref<(ParametricListValue | undefined)[]>();

const { selectedColor, products, parametricsInformation } =
	storeToRefs(useProductStore());
const route = useRoute();
const selectedSize = ref<ParametricListValue>();

const setInitialSecondParametric = ({
	parametricsSecondOne
}: {
	parametricsSecondOne:
		| (ParametricListValue | ParametricListValuewithCount)[]
		| undefined;
}) => {
	let parametricsSecondOneLocal = JSON.parse(
		JSON.stringify(parametricsSecondOne)
	);
	if (products.value) {
		parametricsSecondOneLocal = getAllSecondParametrics({
			parametrics: parametricsSecondOneLocal,
			products: products.value,
			selectedColor: selectedColor.value as ParametricListValue
		});

		sizes.value = parametricsSecondOneLocal;

		const parametrics = route.query.parametrics;
		let parametricsSeed: string | undefined = undefined;

		if (parametrics) {
			parametricsSeed = JSON.parse(JSON.stringify(parametrics));
		}

		selectedSize.value = getFirstSecondParametricWithStock({
			parametrics: parametricsSecondOneLocal,
			products: products.value,
			selectedColor: selectedColor.value as ParametricListValue,
			...(parametricsSeed && { parametricsSeed: parametricsSeed })
		});
	}
};

watchEffect(() => {
	setInitialSecondParametric({
		parametricsSecondOne: parametricsSecondOne
	});
});

watch(selectedSize, () => {
	if (selectedSize.value) {
		useProductStore().addSelectedSecondParametric(selectedSize.value);
	}
});
const setName = computed(() => {
	return parametricsInformation.value.filter(
		(paramInfo) => paramInfo.Code === sizes?.value?.[0]?.ParametricCode
	)[0]?.Name;
});
</script>
